import type { OfferLine } from "@/hooks/use-offers";
import type { OfferPhase } from "@/lib/offer-calc";
import { readOvertimeSplit } from "@/lib/offer-calc";
import {
  PHASE_ORDER,
  fmtPlain,
  fmtQty,
  fmtOvertimeBreakdown,
  exportLineDescription,
} from "@/lib/offer-budget-image-geometry";
import {
  OFFER_EXPORT_COLORS,
  OFFER_EXPORT_FONT_BODY,
  OFFER_EXPORT_FONT_DISPLAY,
  OFFER_EXPORT_RULE_PT,
} from "@/lib/offer-export-brand";

export interface BudgetTableCell {
  text: string;
  colspan?: number;
  align?: "left" | "center" | "right";
  bold?: boolean;
  italic?: boolean;
  color?: string;
  fontFace?: string;
  fontSize?: number;
  /**
   * Skillelinjer. Topp og bunn settes hver for seg fordi SUM-raden skal
   * rammes inn av begge, mens fase-headerne bare har en linje over seg.
   */
  borderTop?: { pt: number; color: string };
  borderBottom?: { pt: number; color: string };
}

/**
 * Skillelinja. Samme vekt overalt, som i malen — SUM skilles ut ved å ha
 * linje både over og under, ikke ved en tyngre strek.
 */
export const TABLE_RULE = {
  pt: OFFER_EXPORT_RULE_PT,
  color: OFFER_EXPORT_COLORS.tableBorder,
} as const;

export type BudgetTableRow = BudgetTableCell[];

export interface BudgetTotalsForExport {
  overhead_amount: number;
  discount_amount: number;
  total: number;
}

export interface BuildBudgetTableRowsOpts {
  visibleLines: OfferLine[];
  phases?: typeof PHASE_ORDER;
  fontSizeRow: number;
  fontSizePhase: number;
  includeTotals: boolean;
  hideOverheadAndDiscount?: boolean;
  totals?: BudgetTotalsForExport;
  overheadPct?: number;
  discountPctText?: string;
}

/** Eksport-etiketter med bindestrek som i Keynote-malen. Editor beholder PHASE_ORDER. */
export function exportPhaseLabel(key: OfferPhase): string {
  if (key === "preprod") return "Pre-produksjon";
  if (key === "postprod") return "Post-produksjon";
  return PHASE_ORDER.find((p) => p.key === key)?.label ?? key;
}

export function phasesForExport(
  phases: typeof PHASE_ORDER = PHASE_ORDER,
): typeof PHASE_ORDER {
  return phases.map((p) => ({ ...p, label: exportPhaseLabel(p.key) }));
}

function cell(
  text: string,
  opts: Omit<BudgetTableCell, "text"> = {},
): BudgetTableCell {
  return {
    text,
    color: OFFER_EXPORT_COLORS.tableText,
    fontFace: OFFER_EXPORT_FONT_BODY,
    ...opts,
  };
}

function emptyQtyCells(
  italic: boolean,
  fontSize: number,
): BudgetTableCell[] {
  return [
    cell("", { align: "right", italic, fontSize }),
    cell("", { align: "center", italic, fontSize }),
  ];
}

/**
 * Bygger én native tabell (fase-headers + linjer + ev. totals) slik at
 * Keynote får ett tabellobjekt, ikke én tekstboks per celle (VIB-2091).
 */
export function buildBudgetTableRows(
  opts: BuildBudgetTableRowsOpts,
): BudgetTableRow[] {
  const {
    visibleLines,
    phases = phasesForExport(),
    fontSizeRow,
    fontSizePhase,
    includeTotals,
    hideOverheadAndDiscount = false,
    totals,
    overheadPct = 0,
    discountPctText = "",
  } = opts;

  const rows: BudgetTableRow[] = [];
  let renderedPhases = 0;

  for (const phase of phases) {
    const phaseLines = visibleLines.filter((l) => l.phase === phase.key);
    if (phaseLines.length === 0) continue;

    // Skillelinje MELLOM fasene: den første gruppa får ingen linje over
    // seg, ellers ville tabellen åpnet med en strek mot ingenting.
    const phaseRule = renderedPhases > 0 ? TABLE_RULE : undefined;
    // FEM ekte celler, ikke én med colspan. Med colspan legger pptxgenjs
    // kanten bare på cella som eier sammenslåingen; de fire hMerge-cellene
    // står uten. PowerPoint trekker da linja over hele det sammenslåtte
    // feltet, men Keynote tegner per celle, så linja ble borte fra
    // fase-headerne mens SUM — som alltid har hatt fem egne celler — viste
    // sin (Erik 30.09). Samme grunn som testene krever kanten på alle fem.
    rows.push([
      cell(exportPhaseLabel(phase.key), {
        fontSize: fontSizePhase,
        fontFace: OFFER_EXPORT_FONT_DISPLAY,
        bold: true,
        borderTop: phaseRule,
      }),
      ...([1, 2, 3, 4] as const).map(() =>
        cell("", {
          fontSize: fontSizePhase,
          fontFace: OFFER_EXPORT_FONT_DISPLAY,
          bold: true,
          borderTop: phaseRule,
        }),
      ),
    ]);
    renderedPhases += 1;

    for (const l of phaseLines) {
      const qty = l.quantity ?? 0;
      const italic = l.cost_type === "external";
      const qtyCells =
        qty > 0
          ? [
              cell(fmtQty(qty), {
                align: "right",
                italic,
                fontSize: fontSizeRow,
              }),
              cell(l.unit ?? "stk", {
                align: "center",
                italic,
                fontSize: fontSizeRow,
              }),
            ]
          : emptyQtyCells(italic, fontSizeRow);

      rows.push([
        cell(exportLineDescription(l.description), {
          italic,
          fontSize: fontSizeRow,
        }),
        ...qtyCells,
        cell("kr", { italic, fontSize: fontSizeRow }),
        cell(fmtPlain(l.amount ?? 0), {
          align: "right",
          italic,
          fontSize: fontSizeRow,
        }),
      ]);

      const split = readOvertimeSplit({
        overtime_split: l.overtime_split ?? null,
      });
      if (split) {
        const breakdown = fmtOvertimeBreakdown(split, qty, l.unit ?? "t");
        rows.push([
          cell(breakdown, {
            colspan: 5,
            italic: true,
            color: OFFER_EXPORT_COLORS.tableMuted,
            fontSize: Math.max(fontSizeRow - 2, 7),
          }),
        ]);
      }
    }
  }

  if (!includeTotals || !totals) return rows;

  // Malen har INGEN linje mellom siste fase og totalene: overhead og
  // rabatt flyter rett videre, og bare SUM rammes inn. Vi hadde en
  // «lukkelinje» her som ikke finnes i `Solvrev_Tilbudsmal_2026_2.key`
  // (Erik 29.09: «linjene ble ikke bra»).

  if (!hideOverheadAndDiscount && totals.overhead_amount > 0) {
    rows.push([
      cell("+ Overheadkostnad", {
        color: OFFER_EXPORT_COLORS.tableMuted,
        fontSize: fontSizeRow,
      }),
      cell(`(${overheadPct} %)`, {
        align: "right",
        color: OFFER_EXPORT_COLORS.tableMuted,
        fontSize: fontSizeRow,
      }),
      cell("", {
        color: OFFER_EXPORT_COLORS.tableMuted,
        fontSize: fontSizeRow,
      }),
      cell("kr", {
        color: OFFER_EXPORT_COLORS.tableMuted,
        fontSize: fontSizeRow,
      }),
      cell(fmtPlain(totals.overhead_amount), {
        align: "right",
        color: OFFER_EXPORT_COLORS.tableMuted,
        fontSize: fontSizeRow,
      }),
    ]);
  }

  if (!hideOverheadAndDiscount && totals.discount_amount > 0) {
    rows.push([
      cell("- rabatt", {
        color: OFFER_EXPORT_COLORS.tableMuted,
        fontSize: fontSizeRow,
      }),
      cell(discountPctText, {
        align: "right",
        color: OFFER_EXPORT_COLORS.tableMuted,
        fontSize: fontSizeRow,
      }),
      cell("", {
        color: OFFER_EXPORT_COLORS.tableMuted,
        fontSize: fontSizeRow,
      }),
      cell("kr", {
        color: OFFER_EXPORT_COLORS.tableMuted,
        fontSize: fontSizeRow,
      }),
      cell(`-${fmtPlain(totals.discount_amount)}`, {
        align: "right",
        color: OFFER_EXPORT_COLORS.tableMuted,
        fontSize: fontSizeRow,
      }),
    ]);
  }

  // SUM rammes inn av en linje både over og under (Erik 29.09).
  const framed = { borderTop: TABLE_RULE, borderBottom: TABLE_RULE } as const;
  rows.push([
    cell("= SUM", {
      bold: true,
      fontSize: fontSizePhase + 1,
      fontFace: OFFER_EXPORT_FONT_DISPLAY,
      ...framed,
    }),
    cell("", {
      bold: true,
      fontFace: OFFER_EXPORT_FONT_BODY,
      fontSize: fontSizePhase + 1,
      ...framed,
    }),
    cell("", {
      bold: true,
      fontFace: OFFER_EXPORT_FONT_BODY,
      fontSize: fontSizePhase + 1,
      ...framed,
    }),
    cell("kr", {
      bold: true,
      fontSize: fontSizePhase + 1,
      fontFace: OFFER_EXPORT_FONT_DISPLAY,
      ...framed,
    }),
    cell(fmtPlain(totals.total), {
      bold: true,
      align: "right",
      fontSize: fontSizePhase + 1,
      fontFace: OFFER_EXPORT_FONT_DISPLAY,
      ...framed,
    }),
  ]);

  return rows;
}
